<template>
  <Container v-if="store.openedPanel === null" full-width-on-mobile class="lg:hidden">
    <div class="image-navigation-wrapper flex items-center relative px-2 py-2 align-items-start">
      <UploadButton
        v-if="store.images.length !== 0"
        @click="uploadFile"
        id="mobile-upload"
        class="mobile-upload-btn sm:!mb-0 !m-0"
      />
      <div class="image-gallery sm:!mt-2 sm:!mb-0 overflow-x-scroll hide-scrollbars">
        <div class="flex image-gallery-wrapper pt-2" id="image-gallery-wrapper">
          <div v-for="(image, index) in images" :key="image.meta.id">
            <ImageButton
              v-if="image.previewResult.state === ProcessingState.Processing"
              :selected="store.selectedImage === image"
              :id="imageId(image)"
              @click="select($event, image, () => {})"
              class="relative"
              loading
            >
              <HTMLImage
                :src="image.meta.preview"
                img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
              />
            </ImageButton>

            <ImageButton
              v-else-if="hasError(image)"
              :selected="store.selectedImage === image"
              :id="imageId(image)"
              @click="
                store.selectedImage === image ? deleteImage(store.selectedImage) : select($event, image, () => {})
              "
            >
              <template #action>
                <TrashIcon
                  v-if="store.selectedImage === image"
                  class="hover:!text-newBlue-primary-foreground active:!text-newBlue-primary-foreground"
                />
                <WarningIcon v-else />
              </template>

              <HTMLImage
                :src="image.meta.preview"
                img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
              />
            </ImageButton>

            <!-- Delete confirmation dialog - only for processed HD images-->
            <Dialog
              v-else-if="store.selectedImage === image && requiresDeleteConfirmation"
              trigger-classes="focus:outline-none"
            >
              <template #trigger>
                <ImageButton
                  :selected="store.selectedImage === image"
                  :id="imageId(image)"
                  @click="select($event, image, () => {})"
                >
                  <HTMLImage
                    :src="image.meta.preview"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                  <template #action>
                    <TrashIcon />
                  </template>
                </ImageButton>
              </template>
              <template v-slot:content="{ closeDialog }">
                <DeleteImageDialog
                  :on-delete="() => deleteImage(store.selectedImage, closeDialog)"
                  :on-cancel="closeDialog"
                />
              </template>
            </Dialog>
            <ImageButton
              v-else
              :selected="store.selectedImage === image"
              :id="imageId(image)"
              @click="
                store.selectedImage === image ? deleteImage(store.selectedImage) : select($event, image, () => {})
              "
            >
              <HTMLImage
                :src="image.meta.preview"
                img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
              />
              <template #action>
                <TrashIcon />
              </template>
            </ImageButton>
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>

<script setup lang="ts">
import { computed, onUpdated } from "vue";
import { Container, Image as HTMLImage, ImageButton, TrashIcon, UploadButton, WarningIcon, Dialog } from "prism";
import { Image, ProcessingState, UploadState } from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import { rbgRemoveImageV100 } from "kaleido-event-tracker";
import DeleteImageDialog from "./editor_components/delete_image_dialog.vue";

import { useEditorStore } from "@/stores/editor_store";
import { useDeleteConfirmation } from "@/composables/delete_confirmation";
import { useMonitorSize } from "@/composables/monitor_size";
const { isSmallTablet } = useMonitorSize();

const store = useEditorStore();
const { requiresDeleteConfirmation } = useDeleteConfirmation();

const images = computed(() => store.images);

onUpdated(() => {
  scrollToSelectedImage();
});

const select = (event: any, image: Image, closeDialog: () => void) => {
  store.selectImage(image);
  closeDialog();
};

const hasError = (image: Image) => {
  return store.imageHasError(image);
};

const deleteImage = (image: Image, closeDialog?: () => void) => {
  Client.deleteImage(image);
  store.deleteImage(image);
  if (closeDialog) closeDialog();
  window.track("Images", "remove_result", "Remove result");
  rbgRemoveImageV100({ image_id: image.meta.id });
};

const imageId = (image: Image) => image?.meta?.id;

const uploadFile = () => {
  window.onUploadFile();
};

const scrollToSelectedImage = () => {
  let child = document.getElementById(imageId(store.selectedImage));
  if (!child) return;

  child.scrollIntoView();
};
</script>

<style scoped lang="scss">
.image-navigation-wrapper {
  gap: 0.5rem;
}
.image-gallery {
  margin-top: -0.5rem !important;
  padding-top: 0.5rem !important;
}
</style>
